/**
 * CapturePreviewModal.tsx
 * Modal to display captured image with zoom controls
 * 
 * Features:
 * - Header with title and close (X) button
 * - ImageViewer for zoomable/pannable display
 * - Footer with Zoom +/- buttons and percentage display
 */

import React, { useState, useCallback } from 'react';
import { X, ZoomIn, ZoomOut } from 'lucide-react';
import { useCaptureAreaStore } from '../../hooks/useCaptureArea';
import { ImageViewer } from './ImageViewer';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export const CapturePreviewModal: React.FC = () => {
  const { isModalOpen, capturedImage, closeModal } = useCaptureAreaStore();
  const [zoom, setZoom] = useState(1);

  // Debug: log state changes
  React.useEffect(() => {
    console.log('[CaptureModal] State changed:', {
      isModalOpen,
      hasCapturedImage: !!capturedImage,
      imageLength: capturedImage?.length || 0,
    });
  }, [isModalOpen, capturedImage]);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  }, []);

  const handleClose = useCallback(() => {
    closeModal();
    setZoom(1); // Reset zoom for next time
  }, [closeModal]);

  // Handle Escape key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, handleClose]);

  if (!isModalOpen || !capturedImage) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60">
      {/* Modal container */}
      <div
        className="bg-gray-800 rounded-lg shadow-2xl flex flex-col overflow-hidden"
        style={{
          width: 'min(90vw, 900px)',
          height: 'min(85vh, 700px)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-700">
          <h2 className="text-white text-sm font-medium">Captured Area</h2>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Close (Esc)"
          >
            <X size={18} />
          </button>
        </div>

        {/* Image viewer area */}
        <div className="flex-1 overflow-hidden">
          <ImageViewer
            src={capturedImage}
            zoom={zoom}
            className="w-full h-full"
          />
        </div>

        {/* Footer with zoom controls */}
        <div className="flex items-center justify-center gap-4 px-4 py-3 bg-gray-900 border-t border-gray-700">
          <button
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="p-1.5 text-gray-300 hover:text-white hover:bg-gray-700 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Zoom Out"
          >
            <ZoomOut size={18} />
          </button>
          
          <span className="text-white text-sm font-mono min-w-[4rem] text-center">
            {Math.round(zoom * 100)}%
          </span>
          
          <button
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="p-1.5 text-gray-300 hover:text-white hover:bg-gray-700 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Zoom In"
          >
            <ZoomIn size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
